import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import {
  FaTimes,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaLock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
  FaIdCard,
} from 'react-icons/fa';

export default function ProfileModal({ isOpen, onClose }) {
  const { user, updateUserProfile } = useAuth();

  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'password'

  // Profile fields
  const [name, setName] = useState('');
  const [mobileNo, setMobileNo] = useState('');
  const [alterMobileNo, setAlterMobileNo] = useState('');
  const [emailId, setEmailId] = useState('');

  // Password fields matching copsuvidha3.22 rules
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user && isOpen) {
      setName(user.name || '');
      setMobileNo(user.mobileNo || '');
      setAlterMobileNo(user.alterMobileNo || '');
      setEmailId(user.emailId || '');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess('');
    }
  }, [user, isOpen]);

  if (!isOpen || !user) return null;

  // Validate password rules (min 8, uppercase, lowercase, number, special char)
  const validatePassword = () => {
    if (!currentPassword) return 'Current password is required';
    if (!newPassword) return 'New password is required';

    const minLength = 8;
    const uppercasePattern = /[A-Z]/;
    const lowercasePattern = /[a-z]/;
    const digitPattern = /[0-9]/;
    const specialCharPattern = /[!@#$%^&*(),.?":{}|<>_+\-=]/;

    if (newPassword.length < minLength) {
      return `New password must be at least ${minLength} characters long`;
    }
    if (!uppercasePattern.test(newPassword)) {
      return 'New password must contain at least one uppercase letter (A-Z)';
    }
    if (!lowercasePattern.test(newPassword)) {
      return 'New password must contain at least one lowercase letter (a-z)';
    }
    if (!digitPattern.test(newPassword)) {
      return 'New password must contain at least one number (0-9)';
    }
    if (!specialCharPattern.test(newPassword)) {
      return 'New password must contain at least one special character (!@#$%^&*...)';
    }
    if (currentPassword === newPassword) {
      return 'New password cannot be the same as current password';
    }
    if (newPassword !== confirmPassword) {
      return 'Confirm password does not match new password';
    }
    return null;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!name.trim()) {
      setError('Name cannot be empty.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        mobileNo: mobileNo.trim() || null,
        alterMobileNo: alterMobileNo.trim() || null,
        emailId: emailId.trim() || null,
      };

      const res = await api.put('/auth/update-profile', payload);
      if (res.data.success) {
        updateUserProfile(res.data.user);
        setSuccess('Profile updated successfully! Name has been changed.');
        setTimeout(() => {
          setSuccess('');
          onClose();
        }, 1200);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const validationError = validatePassword();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    try {
      const res = await api.post('/changePassword', {
        currentPassword,
        newPassword,
      });

      if (res.data.success) {
        setSuccess('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => {
          setSuccess('');
          onClose();
        }, 1200);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '540px' }}
      >
        <div className="modal-header">
          <div>
            <h3 style={{ fontSize: '1.2rem' }}>Account Profile & Settings</h3>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              Logged in as <strong style={{ color: 'var(--text-main)' }}>{user.username}</strong> ({user.role_label})
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '1.2rem',
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Tab Navigation */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid var(--border-color)',
            background: 'rgba(15, 23, 42, 0.4)',
            padding: '0 24px',
          }}
        >
          <button
            type="button"
            onClick={() => {
              setActiveTab('profile');
              setError('');
              setSuccess('');
            }}
            style={{
              padding: '12px 18px',
              border: 'none',
              background: 'none',
              borderBottom: activeTab === 'profile' ? '2px solid var(--primary)' : '2px solid transparent',
              color: activeTab === 'profile' ? 'var(--text-main)' : 'var(--text-muted)',
              fontWeight: 600,
              fontSize: '0.88rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <FaUser />
            <span>Profile Information</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveTab('password');
              setError('');
              setSuccess('');
            }}
            style={{
              padding: '12px 18px',
              border: 'none',
              background: 'none',
              borderBottom: activeTab === 'password' ? '2px solid var(--primary)' : '2px solid transparent',
              color: activeTab === 'password' ? 'var(--text-main)' : 'var(--text-muted)',
              fontWeight: 600,
              fontSize: '0.88rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <FaLock />
            <span>Change Password</span>
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#fca5a5',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px',
              }}
            >
              <FaExclamationTriangle />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                background: 'rgba(16, 185, 129, 0.15)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                color: '#6ee7b7',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px',
              }}
            >
              <FaCheckCircle />
              <span>{success}</span>
            </div>
          )}

          {activeTab === 'profile' ? (
            <form onSubmit={handleProfileSubmit}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px',
                  marginBottom: '16px',
                  padding: '12px',
                  background: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-color)',
                }}
              >
                <div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>Username / ID</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--secondary)' }}>
                    {user.username}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>Role / Jurisdiction</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-main)' }}>
                    {user.role_label}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <FaUser />
                  <span>Full Name / Office Title * (Editable)</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div className="form-group">
                  <label className="form-label">
                    <FaPhone />
                    <span>Primary Mobile</span>
                  </label>
                  <input
                    type="text"
                    maxLength={10}
                    className="form-control"
                    value={mobileNo}
                    onChange={(e) => setMobileNo(e.target.value)}
                    placeholder="10-digit mobile"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <FaPhone />
                    <span>Alternate Mobile</span>
                  </label>
                  <input
                    type="text"
                    maxLength={10}
                    className="form-control"
                    value={alterMobileNo}
                    onChange={(e) => setAlterMobileNo(e.target.value)}
                    placeholder="Alternate mobile"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <FaEnvelope />
                  <span>Email Address</span>
                </label>
                <input
                  type="email"
                  className="form-control"
                  value={emailId}
                  onChange={(e) => setEmailId(e.target.value)}
                  placeholder="officer@domain.gov.in"
                />
              </div>

              <div className="modal-footer" style={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}>
                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Name & Profile'}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label className="form-label">Current Password *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showCurrent ? 'text' : 'password'}
                    className="form-control"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter existing password"
                    required
                    style={{ paddingRight: '40px' }}
                  />
                  <span
                    onClick={() => setShowCurrent(!showCurrent)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-dim)',
                      cursor: 'pointer',
                    }}
                  >
                    {showCurrent ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">New Password *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNew ? 'text' : 'password'}
                    className="form-control"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min 8 chars, 1 uppercase, 1 number, 1 symbol"
                    required
                    style={{ paddingRight: '40px' }}
                  />
                  <span
                    onClick={() => setShowNew(!showNew)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-dim)',
                      cursor: 'pointer',
                    }}
                  >
                    {showNew ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '4px' }}>
                  Must contain 8+ characters, uppercase letter, lowercase letter, number, and special character.
                </span>
              </div>

              <div className="form-group">
                <label className="form-label">Confirm New Password *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    className="form-control"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    style={{ paddingRight: '40px' }}
                  />
                  <span
                    onClick={() => setShowConfirm(!showConfirm)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-dim)',
                      cursor: 'pointer',
                    }}
                  >
                    {showConfirm ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
                {confirmPassword && newPassword === confirmPassword && (
                  <span style={{ fontSize: '0.75rem', color: '#34d399', marginTop: '4px' }}>
                    ✓ Passwords match
                  </span>
                )}
              </div>

              <div className="modal-footer" style={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}>
                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Updating Password...' : 'Save New Password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
